import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export default function EmailTemplatesAdmin() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [form, setForm] = useState({ subject: '', body_text: '', body_html: '' });
  const [meta, setMeta] = useState({ name: '', description: '', variables_hint: '', is_customized: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/email-templates');
      const list = res.data.templates || [];
      setTemplates(list);
      setSelectedKey((prev) => prev || (list[0] && list[0].template_key) || null);
    } catch (err) {
      setError(err.response?.data?.error || t('admin.emailTemplates.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadOne = useCallback(async (key) => {
    if (!key) return;
    setError(null);
    try {
      const res = await axios.get(`/api/email-templates/${key}`);
      const tpl = res.data.template;
      setForm({
        subject: tpl.subject || '',
        body_text: tpl.body_text || '',
        body_html: tpl.body_html || ''
      });
      setMeta({
        name: tpl.name || key,
        description: tpl.description || '',
        variables_hint: tpl.variables_hint || '',
        is_customized: Boolean(tpl.is_customized)
      });
    } catch (err) {
      setError(err.response?.data?.error || t('admin.emailTemplates.loadError'));
    }
  }, [t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedKey) loadOne(selectedKey);
  }, [selectedKey, loadOne]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedKey) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await axios.put(`/api/email-templates/${selectedKey}`, form);
      const tpl = res.data.template;
      setMeta((m) => ({ ...m, is_customized: Boolean(tpl.is_customized) }));
      setMessage(t('admin.emailTemplates.saved'));
      loadList();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.emailTemplates.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedKey) return;
    if (!window.confirm(t('admin.emailTemplates.resetConfirm'))) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await axios.post(`/api/email-templates/${selectedKey}/reset`);
      const tpl = res.data.template;
      setForm({
        subject: tpl.subject || '',
        body_text: tpl.body_text || '',
        body_html: tpl.body_html || ''
      });
      setMeta({
        name: tpl.name || selectedKey,
        description: tpl.description || '',
        variables_hint: tpl.variables_hint || '',
        is_customized: false
      });
      setMessage(t('admin.emailTemplates.resetDone'));
      loadList();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.emailTemplates.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !templates.length) {
    return <p className="admin-muted">{t('admin.loading')}</p>;
  }

  return (
    <div className="dashboard-section email-templates-admin">
      <div className="section-header">
        <h2>{t('admin.emailTemplates.title')}</h2>
        <p className="section-lead">{t('admin.emailTemplates.lead')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="email-templates-layout">
        <aside className="email-templates-sidebar card">
          <h3>{t('admin.emailTemplates.listTitle')}</h3>
          <ul className="email-templates-list">
            {templates.map((tpl) => (
              <li key={tpl.template_key}>
                <button
                  type="button"
                  className={`email-templates-list-btn ${selectedKey === tpl.template_key ? 'active' : ''}`}
                  onClick={() => setSelectedKey(tpl.template_key)}
                >
                  <span className="email-templates-list-name">{tpl.name}</span>
                  {tpl.is_customized && (
                    <span className="email-templates-badge">{t('admin.emailTemplates.customized')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {selectedKey && (
          <div className="email-templates-editor card">
            <h3>{meta.name}</h3>
            {meta.description && <p className="form-hint">{meta.description}</p>}
            {meta.variables_hint && (
              <div className="email-templates-vars">
                <strong>{t('admin.emailTemplates.variables')}</strong>
                <code>{meta.variables_hint}</code>
              </div>
            )}

            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>{t('admin.emailTemplates.subject')}</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.emailTemplates.bodyText')}</label>
                <textarea
                  value={form.body_text}
                  onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                  rows={14}
                  required
                  spellCheck={false}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.emailTemplates.bodyHtml')}</label>
                <textarea
                  value={form.body_html}
                  onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                  rows={16}
                  required
                  spellCheck={false}
                  className="email-templates-html-input"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('admin.emailTemplates.saving') : t('admin.emailTemplates.save')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={saving}>
                  {t('admin.emailTemplates.reset')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
